import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Loader2, Search, X, Store as StoreIcon, Globe } from 'lucide-react';
import type { StoreListItem } from './types';
import { PLATFORM_LABELS } from './types';

import shopifyLogo from '../../assets/platforms/shopify.svg';
import metaLogo from '../../assets/platforms/meta.svg';
import googleAdsLogo from '../../assets/platforms/google-ads.svg';
import tiktokLogo from '../../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../../assets/platforms/pinterest.svg';
import klaviyoLogo from '../../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../../assets/platforms/amazon-seller.svg';
import ga4Logo from '../../assets/platforms/ga4.svg';

const PLATFORM_ICONS: Record<string, string> = {
  shopify: shopifyLogo,
  meta: metaLogo,
  google_ads: googleAdsLogo,
  tiktok_business: tiktokLogo,
  tiktok_shop: tiktokShopLogo,
  pinterest: pinterestLogo,
  klaviyo: klaviyoLogo,
  amazon_ads: amazonAdsLogo,
  amazon_seller: amazonSellerLogo,
  ga4: ga4Logo,
};

export function StoreSelector({
  selectedStoreId,
  onSelect,
}: {
  readonly selectedStoreId: number | null;
  readonly onSelect: (id: number, name: string) => void;
}) {
  const [search, setSearch] = useState('');

  const { data: stores, isLoading } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });

  const filtered = useMemo(() => {
    if (!stores) return [];
    if (!search.trim()) return stores;
    const q = search.toLowerCase();
    return stores.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.id).includes(q),
    );
  }, [stores, search]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <StoreIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Select Store</h3>
        </div>
        {stores && (
          <span className="text-xs text-gray-400 dark:text-gray-500">{stores.length} store{stores.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or ID..."
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading stores...
        </div>
      ) : (
        <div className="max-h-56 overflow-y-auto space-y-1">
          {filtered.map((store) => {
            const isSelected = selectedStoreId === store.id;
            return (
              <button
                key={store.id}
                onClick={() => onSelect(store.id, store.name)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all group ${
                  isSelected
                    ? 'bg-emerald-50 border border-emerald-200 ring-1 ring-emerald-100'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`font-medium truncate ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>
                      {store.name}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                      isSelected ? 'text-emerald-600 bg-emerald-100' : 'text-gray-400 bg-gray-100'
                    }`}>
                      #{store.id}
                    </span>
                  </div>
                  {store.integrations.length > 0 && (
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {store.integrations.slice(0, 4).map((platform) => {
                        const icon = PLATFORM_ICONS[platform];
                        const label = PLATFORM_LABELS[platform] ?? platform;
                        return icon ? (
                          <img key={platform} src={icon} alt={label} className="w-4 h-4" title={label} />
                        ) : (
                          <Globe key={platform} className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                        );
                      })}
                      {store.integrations.length > 4 && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-0.5">
                          +{store.integrations.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No stores found</p>
          )}
        </div>
      )}
    </div>
  );
}
